import * as CDM from 'trimble-connect-sdk';
import { Accessor, Component, createContext, JSX, useContext } from 'solid-js';
import { createStore } from 'solid-js/store';
import { useConfiguration } from './ConfigurationProvider';
import { mapServiceRegionToRegion } from './utils';

export interface ProjectRegionData {
  regions: CDM.Server[];
  region: string;
  isLoading: boolean;
  project: CDM.Project | null;
  error: string | null;
}

export interface ProjectRegionActions {
  // A location that matches a server limits the lookup to that server.
  fetchRegionsAndProject(
    projectId: string,
    accessToken: string,
    location?: string
  ): Promise<void>;
  mapServiceRegionToRegion(location: string): string;
}

export interface UseProjectRegionDataReturn {
  data: Accessor<ProjectRegionData>;
  actions: ProjectRegionActions;
}

const getProjectFromServer = async (
  projectId: string,
  server: CDM.Server
): Promise<CDM.Project | null> => {
  try {
    const projectResponse = await CDM.TCPSClient.getProject(projectId, server);
    return projectResponse.data;
  } catch {
    return null;
  }
};

const findProjectInAnyRegion = async (
  projectId: string,
  servers: readonly CDM.Server[]
): Promise<CDM.Project | null> => {
  try {
    // The SDK iterates every server internally when no `server` arg is
    // provided, so this single call already handles cross-region lookup.
    const projectResponse = await CDM.TCPSClient.getProject(projectId);
    return projectResponse.data;
  } catch {
    // Fallback: explicit per-region iteration in case the SDK's internal
    // iteration threw before reaching the project's region.
    for (const server of servers) {
      const projectData = await getProjectFromServer(projectId, server);
      if (projectData) return projectData;
    }
    return null;
  }
};

// Single context that backs every consumer hook in this package.
// Backed by a `createStore` so reads on the returned data (e.g.
// `projectRegion.region`) are fine-grained reactive when used inside
// JSX, effects, or memos -- the previous signal-snapshot accessor
// returned a frozen object literal and silently lost reactivity at the
// call site.
const ProjectRegionContext = createContext<UseProjectRegionDataReturn>();

export interface ProjectRegionProviderProps {
  children?: JSX.Element;
}

export const ProjectRegionProvider: Component<ProjectRegionProviderProps> = (
  props: ProjectRegionProviderProps
) => {
  const configuration = useConfiguration();
  const [state, setState] = createStore<ProjectRegionData>({
    regions: [],
    region: '',
    isLoading: false,
    project: null,
    error: null,
  });

  const fetchRegionsAndProject = async (
    projectId: string,
    accessToken: string,
    location?: string
  ): Promise<void> => {
    // Skip if we've already loaded this exact project successfully.
    // On failure `state.project` stays null, so retries proceed naturally.
    if (state.project?.id === projectId) {
      return;
    }

    try {
      setState({ isLoading: true, error: null });

      // Configure TCPSClient with environment-specific domain (initial call before we know region)
      const tcpsApiDomain = configuration.getTcpsApiDomain();
      CDM.TCPSClient.config.serviceUri = `${tcpsApiDomain}/`;
      CDM.TCPSClient.config.credentials = { token: accessToken };

      const regionsResponse = await CDM.TCPSClient.listServers();
      const serverRegions = regionsResponse.data;
      setState('regions', serverRegions);

      const projectServer =
        location === undefined
          ? undefined
          : serverRegions.find((server) => server.location === location);

      const projectData = projectServer
        ? (await CDM.TCPSClient.getProject(projectId, projectServer)).data
        : await findProjectInAnyRegion(projectId, serverRegions);

      if (projectData) {
        if (projectData.location) {
          setState({
            project: projectData,
            region: mapServiceRegionToRegion(projectData.location),
          });
        } else {
          console.warn('Could not determine region for project', projectId);
          setState('project', projectData);
        }
      } else {
        console.error('Could not fetch project from any region');
        setState('error', 'Could not fetch project from any region');
      }
    } catch (err) {
      console.error('Error fetching regions or project:', err);
      setState(
        'error',
        err instanceof Error ? err.message : 'Unknown error occurred'
      );
    } finally {
      setState('isLoading', false);
    }
  };

  // Returns the live store proxy. Property reads (`.region`, `.isLoading`,
  // ...) track fine-grained reactive dependencies on the underlying store.
  const data: Accessor<ProjectRegionData> = () => state;

  const actions: ProjectRegionActions = {
    fetchRegionsAndProject,
    mapServiceRegionToRegion,
  };

  const contextValue: UseProjectRegionDataReturn = { data, actions };

  return (
    <ProjectRegionContext.Provider value={contextValue}>
      {props.children}
    </ProjectRegionContext.Provider>
  );
};

/**
 * Returns the full `{ data, actions }` pair from the surrounding
 * `<ProjectRegionProvider>`. The same instance is shared with every other
 * consumer in the tree. Reads on `data()` track fine-grained reactive
 * dependencies on the underlying store.
 */
export const useProjectRegionData = (): UseProjectRegionDataReturn => {
  const context = useContext(ProjectRegionContext);
  if (context === undefined) {
    throw new Error(
      'useProjectRegionData must be used within a ProjectRegionProvider'
    );
  }
  return context;
};

/**
 * Convenience accessor that returns the live `ProjectRegionData` store.
 * Reads on the returned object (e.g. `projectRegion.region`) are
 * fine-grained reactive when used inside JSX, effects, or memos.
 */
export const useProjectRegion = (): ProjectRegionData => {
  return useProjectRegionData().data();
};
